import { AutoCompleteCompleteEvent, AutoCompleteModule } from 'primeng/autocomplete';
import { Component, forwardRef, Input, OnChanges, OnDestroy, SimpleChanges } from '@angular/core';
import { ControlValueAccessor, FormsModule, NG_VALUE_ACCESSOR } from '@angular/forms';
import { MessageModule } from 'primeng/message';
import { Subscription } from 'rxjs';
import { UsersForAccountManagementFieldsFragment, UsersForAccountManagementGQL } from '../../graphql/gql';

// Upper bound on the number of users offered for one account. Accounts with more users than this
// are rare; when it happens the template shows a hint instead of silently truncating. The number
// is interpolated into that hint, so it stays in sync with the translations.
const MAX_USERS = 200;

// Multi-select over the active users of a single account, exposed as a form control whose value is
// an array of user IDs. The offered account is driven by `clientNumber`; when that is null the
// backend falls back to the current user's own account.
@Component({
  selector: 'app-account-user-selector',
  imports: [
    AutoCompleteModule,
    FormsModule,
    MessageModule
  ],
  templateUrl: './account-user-selector.component.html',
  styleUrl: './account-user-selector.component.css',
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => AccountUserSelectorComponent),
      multi: true
    }
  ]
})
export class AccountUserSelectorComponent implements ControlValueAccessor, OnChanges, OnDestroy {
  @Input() clientNumber: number | null = null;

  readonly maxUsers = MAX_USERS;

  selectedUsers: UsersForAccountManagementFieldsFragment[] = [];
  suggestions: UsersForAccountManagementFieldsFragment[] = [];
  loading = false;
  loadFailed = false;
  disabled = false;
  truncated = false;

  placeholder = $localize`:account user selector|Placeholder:Select users`;

  private readonly noUsersMessage = $localize`:account user selector|No matching users:No matching users`;
  private readonly loadingMessage = $localize`:account user selector|Loading users:Loading users…`;
  private readonly loadFailedMessage = $localize`:account user selector|Load failed:The users of this account could not be loaded.`;

  private users: UsersForAccountManagementFieldsFragment[] = [];
  private usersLoaded = false;
  private pendingUserIds: string[] = [];
  private fetchSubscription?: Subscription;

  onChanged: (value: string[]) => void = () => {};
  onTouched: () => void = () => {};

  constructor(
    private usersForAccountManagementGQL: UsersForAccountManagementGQL
  ) {}

  // The dropdown is the only place a failed or pending fetch would otherwise be invisible: without
  // this the panel claims there are no matching users while the request is still on its way.
  get emptyMessage(): string {
    if (this.loading) {
      return this.loadingMessage;
    }

    return this.loadFailed ? this.loadFailedMessage : this.noUsersMessage;
  }

  ngOnChanges(changes: SimpleChanges): void {
    const change = changes['clientNumber'];

    if (!change || change.previousValue === change.currentValue) {
      return;
    }

    this.fetchUsers(!change.firstChange);
  }

  ngOnDestroy(): void {
    this.fetchSubscription?.unsubscribe();
  }

  writeValue(value: string[] | null | undefined): void {
    this.pendingUserIds = value ?? [];
    this.resolvePendingSelection();
  }

  registerOnChange(fn: (value: string[]) => void) {
    this.onChanged = fn;
  }

  registerOnTouched(fn: () => void) {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean) {
    this.disabled = isDisabled;
  }

  // The list mixes users of the parent account with users of the requester's own account, so the
  // account is part of the label to tell otherwise identical-looking entries apart. It is also
  // what makes the account searchable, since `complete` filters on this string.
  //
  // Declared as an arrow property because PrimeNG resolves `optionLabel` by calling it without a
  // receiver; the lexical binding keeps `this` intact either way.
  userLabel = (user: UsersForAccountManagementFieldsFragment): string => {
    const name = `${user.firstname} ${user.lastname} (${user.email})`;
    const account = user.account;

    return account?.clientNumber
      ? `${name} — ${account.clientNumber} ${account.company}`
      : name;
  };

  complete(event: AutoCompleteCompleteEvent) {
    const query = event.query.trim().toLowerCase();

    this.suggestions = query
      ? this.users.filter(user => this.userLabel(user).toLowerCase().includes(query))
      : [...this.users];
  }

  // The "clear" button emits `null` rather than an empty array — `AutoComplete#clear` calls
  // `updateModel(null)` regardless of `multiple` — while removing chips one by one emits an
  // array. Normalise so the rest of the component only ever deals with a list.
  onSelectionChange(users: UsersForAccountManagementFieldsFragment[] | null) {
    this.selectedUsers = users ?? [];
    this.propagateChange();
  }

  handleBlur() {
    this.onTouched();
  }

  // Re-fetches the candidates for the current `clientNumber`. Picked users survive the switch as
  // long as they are still candidates — which the requester's own colleagues always are — and
  // only those who belonged solely to the previous parent account are dropped. See BNDDEV-10062.
  private fetchUsers(emitAfterFetch: boolean) {
    this.fetchSubscription?.unsubscribe();

    this.users = [];
    this.suggestions = [];
    this.usersLoaded = false;
    this.truncated = false;
    this.loadFailed = false;
    this.loading = true;

    this.fetchSubscription = this.usersForAccountManagementGQL
      .fetch({ parentClientNumber: this.clientNumber, first: MAX_USERS })
      .subscribe({
        next: (result) => {
          const connection = result.data.usersForAccountManagement;

          this.users = connection.nodes?.filter(node => !!node) ?? [];
          this.truncated = (connection.totalCount ?? 0) > this.users.length;
          this.usersLoaded = true;
          this.loading = false;

          // Narrows the selection to the users which are still candidates.
          this.resolvePendingSelection();

          if (emitAfterFetch) {
            // Emitted here rather than in `ngOnChanges` so the write-back to the parent form
            // happens outside the change detection pass that produced the parent change.
            this.propagateChange();
          }
        },
        error: () => {
          this.users = [];
          this.usersLoaded = false;
          this.loading = false;
          this.loadFailed = true;

          // The selection is deliberately left alone: a failed fetch is no reason to discard what
          // the user picked.
          if (emitAfterFetch) {
            this.propagateChange();
          }
        }
      });
  }

  // `writeValue` only carries IDs; the chips need the user objects, which arrive with the fetch.
  private resolvePendingSelection() {
    if (!this.usersLoaded) {
      return;
    }

    this.selectedUsers = this.users.filter(user => this.pendingUserIds.includes(user.id));
  }

  private propagateChange() {
    this.pendingUserIds = this.selectedUsers.map(user => user.id);
    this.onChanged(this.pendingUserIds);
  }
}
